import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { LocationEnvironmentApi } from '../../../../../../../../core/api/location-environment/location-environment.api';
import { SearchLocationEnvironmentParams } from '../../../../../../../../core/api/location-environment/location-environment.type';
import { LocationSectorApi } from '../../../../../../../../core/api/location-sectors/location-sectors.api';
import { ArchiveEnvironmentDialogService } from '../../../../../../../../core/components/archive-environment-dialog/archive-environment-dialog.service';
import { ActionButton, ActionTypeEnum, TreeViewItem } from '../../../../../../../../core/components/fc-tree-view/fc-tree.types';
import { Location } from '../../../../../../../../core/interfaces';
import { LocationEnvironment } from '../../../../../../../../core/interfaces/locations-environments';
import { fetchAllInBatches } from '../../../../../../../../core/utils/fetch-all-in-batches';
import { LinkEquipmentDialogService } from '../../../../../dialog-link-equipment/dialog-link-equipment.service';
import { DialogLocationEnvironmentComponent, DialogLocationEnvironmentData, DialogLocationEnvironmentResult } from '../../../../../dialog-location-environment/dialog-location-environment.component';
import { EquipmentLinkService } from '../equipment/equipment.service';

@Injectable({
  providedIn: 'root'
})
export class LocationLinkEnvironmentService {
  public constructor(
    private readonly locationEnvironmentApi: LocationEnvironmentApi,
    private readonly locationSectorApi: LocationSectorApi,
    private readonly translateService: TranslateService,
    private readonly matDialog: MatDialog,
    private readonly equipmentLinkService: EquipmentLinkService,
    private readonly archiveEnvironmentDialogService: ArchiveEnvironmentDialogService,
    private readonly linkEquipmentDialogService: LinkEquipmentDialogService
  ) {}

  public async searchLocationEnvironments(params?: SearchLocationEnvironmentParams): Promise<LocationEnvironment[]> {
    return fetchAllInBatches(this.locationEnvironmentApi, params);
  }

  public formatEnvironments(environments: LocationEnvironment[], parent: TreeViewItem): TreeViewItem[] {
    const formattedEnvironments: TreeViewItem[] = [];

    for (const environment of environments) {
      const formattedEnvironment: TreeViewItem = {
        id: environment.id,
        parent: parent,
        text: environment.name,
        description: environment.description,
        searchTerm: [environment.name, environment.description].filter(Boolean).join(' ').toLowerCase(),
        archived: environment.archived,
        archivedText: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVED'),
        icon: 'environment',
        metadata: 'environment',
        action: {
          type: ActionTypeEnum.menu,
          buttons: this.environmentsButtons(environment)
        }
      };

      formattedEnvironment.children = this.equipmentLinkService.formatEquipments(environment.equipments, formattedEnvironment);

      formattedEnvironments.push(formattedEnvironment);
    }

    return formattedEnvironments;
  }

  private environmentsButtons(locationEnvironment: LocationEnvironment): ActionButton[] {
    return [
      {
        text: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.EDIT_ENVIRONMENT'),
        icon: 'edit',
        color: '#676767',
        action: async () => {
          await this.openEditLocationEnvironmentDialog({ locationEnvironmentId: locationEnvironment.id });
        }
      },
      {
        text: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.LINK_EQUIPMENT'),
        icon: 'equipment',
        color: '#676767',
        action: async () => {
          await this.openLinkEquipmentDialog({
            locationEnvironment
          });
        }
      },
      {
        text: locationEnvironment.archived ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNARCHIVE_ENVIRONMENT') : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVE_ENVIRONMENT'),
        icon: locationEnvironment.archived ? 'unarchive_outline' : 'archive_outline',
        color: '#676767',
        action: async () => {
          await this.openArchiveOrUnarchiveLocationEnvironmentDialog({
            locationEnvironment
          });
        }
      },
    ];
  }

  public async openLocationEnvironmentDialog({ location, locationSectorId, locationEnvironmentId }: { location?: Location, locationSectorId?: string, locationEnvironmentId?: string }): Promise<DialogLocationEnvironmentResult> {
    if (locationEnvironmentId) {
      return this.openEditLocationEnvironmentDialog({ locationEnvironmentId });
    } else {
      return this.openCreateLocationEnvironmentDialog({ location, locationSectorId });
    }
  }

  public async openCreateLocationEnvironmentDialog({ location, locationSectorId }: { location: Location, locationSectorId: string }): Promise<DialogLocationEnvironmentResult> {
    const locationEnvironment = {
      id: null,
      name: null,
      description: null,
      qrCode: null,
      location: location,
      locationSector: locationSectorId ? await this.locationSectorApi.get(locationSectorId) : null,
    };

    const result = await this.matDialog
      .open<DialogLocationEnvironmentComponent, DialogLocationEnvironmentData, DialogLocationEnvironmentResult>(DialogLocationEnvironmentComponent, {
        panelClass: 'dialog-location-environment',
        data: {
          locationEnvironment
        }
      })
      .afterClosed()
      .toPromise();

    if (result && result.reload) {
      window.emitEvent('location-links::reload-tree-view');
      return result;
    }
  }

  public async openEditLocationEnvironmentDialog({ locationEnvironmentId }: { locationEnvironmentId: string }): Promise<DialogLocationEnvironmentResult> {
    const locationEnvironment = await this.locationEnvironmentApi.get(locationEnvironmentId);
    locationEnvironment.location = locationEnvironment.locationSector.location;

    const result = await this.matDialog
      .open<DialogLocationEnvironmentComponent, DialogLocationEnvironmentData, DialogLocationEnvironmentResult>(DialogLocationEnvironmentComponent, {
        panelClass: 'dialog-location-environment',
        data: {
          locationEnvironment
        }
      })
      .afterClosed()
      .toPromise();

    if (result && result.reload) {
      window.emitEvent('location-links::reload-tree-view');
      return result;
    }
  }


  private async openArchiveOrUnarchiveLocationEnvironmentDialog({ locationEnvironment }: { locationEnvironment: LocationEnvironment }): Promise<void> {

    const result = await this.archiveEnvironmentDialogService.open({
      sectorId: locationEnvironment.id,
      archived: locationEnvironment.archived,
      name: locationEnvironment.name
    });

    if (!result || !result.reload) {
      return;
    }

    window.emitEvent('location-links::reload-tree-view');

  }

  private async openLinkEquipmentDialog({ locationEnvironment }: { locationEnvironment: LocationEnvironment }): Promise<void> {

    const result = await this.linkEquipmentDialogService.open({
      locationId: locationEnvironment.locationSector.location.id,
      locationSectorId: locationEnvironment.locationSector.id,
      locationEnvironmentId: locationEnvironment.id,
    });

    if (!result || !result.reload) {
      return;
    }

    window.emitEvent('location-links::reload-tree-view');

  }

}
